import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    Gauge, RefreshCcw, ArrowRight, CheckCircle2, AlertTriangle,
    ShieldCheck, ChevronRight,
} from "lucide-react";

/**
 * Data Completeness / Health Score page.
 *
 * Consumes the existing /api/data-health/score signals: card completeness,
 * statement freshness, expense categorisation, receipt attachment,
 * Gmail sync, expiry planning, bank snapshots, bill invoice freshness.
 * Renders each signal with its individual score, weight, current gap and
 * a direct fix link. A single number at the top summarises everything.
 */

const SCORE_TONE = (s) => {
    if (s >= 90) return { label: "Excellent", cls: "text-emerald-700 bg-emerald-50 border-emerald-200", ring: "stroke-emerald-500" };
    if (s >= 75) return { label: "Healthy", cls: "text-blue-700 bg-blue-50 border-blue-200", ring: "stroke-blue-500" };
    if (s >= 50) return { label: "Needs attention", cls: "text-amber-700 bg-amber-50 border-amber-200", ring: "stroke-amber-500" };
    return { label: "Critical", cls: "text-rose-700 bg-rose-50 border-rose-200", ring: "stroke-rose-500" };
};

const SIGNAL_LABEL = {
    card_completeness: "Card master completeness",
    statement_freshness: "Statement freshness",
    expense_categorization: "Expense categorisation",
    receipt_attachment: "Receipt attachment rate",
    gmail_sync: "Gmail auto-sync",
    expiry_plan: "Card expiry planning",
    bank_snapshots: "Bank balance snapshots",
    bill_invoice_freshness: "Bill invoice freshness",
};

function RingGauge({ score }) {
    const tone = SCORE_TONE(score);
    const R = 52;
    const C = 2 * Math.PI * R;
    const offset = C - (Math.max(0, Math.min(100, score)) / 100) * C;
    return (
        <div className="relative w-32 h-32">
            <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                <circle cx="60" cy="60" r={R} strokeWidth="10" className="stroke-stone-100 fill-none" />
                <circle
                    cx="60" cy="60" r={R} strokeWidth="10"
                    strokeDasharray={C}
                    strokeDashoffset={offset}
                    className={`${tone.ring} fill-none transition-all duration-700`}
                    strokeLinecap="round"
                />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center flex-col">
                <div className="text-3xl font-bold tabular-nums text-stone-900" data-testid="dh-score">{score}</div>
                <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">out of 100</div>
            </div>
        </div>
    );
}

function SignalCard({ sig }) {
    const tone = SCORE_TONE(sig.score);
    const done = sig.score >= 90;
    return (
        <div
            className="bg-white border border-stone-200 rounded-2xl p-5 flex flex-col gap-3"
            data-testid={`signal-${sig.key}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold mb-1">
                        {SIGNAL_LABEL[sig.key] || sig.key}
                    </div>
                    <div className="text-lg font-bold text-stone-900 tabular-nums">
                        {sig.score}
                        <span className="text-stone-400 text-sm font-normal">/100</span>
                        <span className="text-[11px] text-stone-500 ml-2 font-normal">weight {sig.weight}%</span>
                    </div>
                </div>
                {done ? (
                    <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
                ) : (
                    <AlertTriangle size={16} className="text-amber-500 shrink-0" />
                )}
            </div>
            <div className="w-full bg-stone-100 rounded-full h-1.5 overflow-hidden">
                <div
                    className={`h-1.5 ${tone.ring.replace("stroke-", "bg-")} transition-all duration-500`}
                    style={{ width: `${sig.score}%` }}
                />
            </div>
            <div className="text-[13px] text-stone-600 leading-relaxed">{sig.detail}</div>
            {sig.gap && (
                <Link
                    to={sig.gap.path || "/"}
                    className="mt-1 inline-flex items-center justify-between gap-2 text-[13px] font-medium text-stone-800 hover:text-stone-900 border border-stone-200 rounded-lg px-3 py-2 hover:border-stone-400 transition-colors"
                    data-testid={`signal-fix-${sig.key}`}
                >
                    <span className="truncate">{sig.gap.title}</span>
                    <span className="inline-flex items-center gap-1 shrink-0 text-stone-500">
                        {sig.gap.cta || "Fix"} <ChevronRight size={12} />
                    </span>
                </Link>
            )}
        </div>
    );
}

export default function DataHealth() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: d } = await api.get("/data-health/score");
            setData(d);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const score = data?.score ?? 0;
    const tone = SCORE_TONE(score);

    return (
        <div className="max-w-[1200px] mx-auto" data-testid="data-health-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow">Command Centre</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Data completeness.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-xl">
                        How much of your finance data is actually captured. The higher this score, the more
                        VAULT&apos;s reports, forecasts and Copilot answers you can trust.
                    </p>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-stone-200 text-sm text-stone-700 hover:border-stone-400 transition-colors"
                    data-testid="dh-refresh-btn"
                >
                    <RefreshCcw size={13} /> Refresh
                </button>
            </div>

            {loading && !data ? (
                <div className="text-sm text-stone-500">Loading score…</div>
            ) : data && (
                <>
                    {/* Hero card */}
                    <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-6 flex items-center gap-6 flex-wrap" data-testid="dh-hero">
                        <RingGauge score={score} />
                        <div className="flex-1 min-w-[240px]">
                            <span
                                className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest px-2 py-1 rounded-full border ${tone.cls}`}
                            >
                                <Gauge size={11} /> {tone.label}
                            </span>
                            <div className="mt-2 text-lg font-semibold text-stone-900">
                                {score >= 90
                                    ? "Your books are pristine. Reports can be trusted end-to-end."
                                    : score >= 75
                                      ? "Mostly complete — a few pockets to tighten up."
                                      : score >= 50
                                        ? "Half the picture is in. Fix the top gaps below to close the loop."
                                        : "Fill the essentials before relying on any dashboard number."}
                            </div>
                            <div className="text-[12px] text-stone-500 mt-1">
                                Score = weighted average of {data.signals?.length ?? 0} signals. Fix the biggest-impact gaps first.
                            </div>
                        </div>
                    </div>

                    {/* Top gaps */}
                    {Array.isArray(data.top_gaps) && data.top_gaps.length > 0 && (
                        <section className="mb-8">
                            <h2 className="text-lg font-semibold text-stone-900 mb-3">Top gaps · biggest score lift first</h2>
                            <div className="bg-white border border-stone-200 rounded-2xl divide-y divide-stone-100">
                                {data.top_gaps.map((g, i) => (
                                    <Link
                                        key={`${g.signal}-${i}`}
                                        to={g.path || "/"}
                                        className="flex items-center gap-4 px-4 py-3 hover:bg-stone-50 transition-colors"
                                        data-testid={`gap-row-${g.signal}`}
                                    >
                                        <span className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                                            <AlertTriangle size={14} />
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm text-stone-900 truncate">{g.title}</div>
                                            <div className="text-[11px] text-stone-500">
                                                Signal: <span className="capitalize">{(SIGNAL_LABEL[g.signal] || g.signal).toLowerCase()}</span>
                                            </div>
                                        </div>
                                        <span className="text-[12px] text-stone-500 shrink-0 inline-flex items-center gap-1">
                                            {g.cta || "Fix"} <ArrowRight size={12} />
                                        </span>
                                    </Link>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Signal grid */}
                    <section>
                        <h2 className="text-lg font-semibold text-stone-900 mb-3">Signal breakdown</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {(data.signals || []).map((s) => <SignalCard key={s.key} sig={s} />)}
                        </div>
                    </section>

                    {score >= 100 && (
                        <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center">
                            <ShieldCheck size={22} className="text-emerald-600 mx-auto mb-2" />
                            <div className="font-display text-2xl text-emerald-800">Perfect score.</div>
                            <div className="text-sm text-emerald-700 mt-2">
                                Every signal is at 100. Every report you export is fully backed by data.
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
